import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PrintIcon from '@mui/icons-material/Print';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EmailIcon from '@mui/icons-material/Mail';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { EventType, Meet, MeetEvent, Registration, Student } from '../api/types';

const HIGH_SCHOOL_CODES = ['100M', '200M', '400M', '800M', '1500M', '4X100M', 'LJ', 'HJ', 'SP'];
const MIDDLE_SCHOOL_CODES = ['100M', '200M', '400M', '800M', '4X100M', 'LJ', 'SP'];
const YOUTH_CODES = ['60M', '100M', '200M', '400M', '800M', 'LJ'];

export default function PrepareMeetPage() {
  const { id } = useParams();
  const meetId = Number(id);
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [selected, setSelected] = useState<Record<number, Set<number>>>({});

  const meetQuery = useQuery<Meet>({ queryKey: ['meets', meetId], queryFn: async () => (await api.get(`/meets/${meetId}`)).data });
  const studentsQuery = useQuery<Student[]>({ queryKey: ['students'], queryFn: async () => (await api.get('/students')).data });
  const eventsQuery = useQuery<MeetEvent[]>({ queryKey: ['meets', meetId, 'events'], queryFn: async () => (await api.get(`/meet-events/by-meet/${meetId}`)).data });
  const eventTypesQuery = useQuery<EventType[]>({ queryKey: ['event-types'], queryFn: async () => (await api.get('/event-types')).data });
  const registrationsQuery = useQuery<Registration[]>({
    queryKey: ['meets', meetId, 'registrations-grid'],
    enabled: !!eventsQuery.data,
    queryFn: async () => {
      const events = eventsQuery.data ?? [];
      const responses = await Promise.all(events.map((ev) => api.get<Registration[]>(`/registrations/by-meet-event/${ev.id}`).then((r) => r.data)));
      return responses.flat();
    },
  });

  const students = studentsQuery.data ?? [];
  const events = eventsQuery.data ?? [];

  useEffect(() => {
    if (!registrationsQuery.data) return;
    const next: Record<number, Set<number>> = {};
    for (const s of studentsQuery.data ?? []) next[s.id] = new Set<number>();
    for (const r of registrationsQuery.data) {
      next[r.student_id] ??= new Set<number>();
      next[r.student_id].add(r.meet_event_id);
    }
    setSelected(next);
  }, [registrationsQuery.data, studentsQuery.data]);

  const addTemplate = useMutation({
    mutationFn: async (codes: string[]) => {
      const existingCodes = new Set(events.map((ev) => ev.event_type.code));
      const types = eventTypesQuery.data ?? [];
      for (const code of codes) {
        if (existingCodes.has(code)) continue;
        const eventType = types.find((t) => t.code === code);
        if (!eventType) continue;
        await api.post(`/meet-events/by-meet/${meetId}`, {
          event_type_id: eventType.id,
          division: 'open',
        });
      }
    },
    onSuccess: () => {
      toast({ message: 'Template events added', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['meets', meetId, 'events'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const saveGrid = useMutation({
    mutationFn: async () => {
      const entries = students.map((s) => ({
        student_id: s.id,
        meet_event_ids: Array.from(selected[s.id] ?? []),
      }));
      return (await api.post('/registrations/bulk-grid', { meet_id: meetId, entries })).data;
    },
    onSuccess: (data) => {
      toast({ message: `Saved ${data.registration_count} entries`, severity: 'success' });
      qc.invalidateQueries({ queryKey: ['meets', meetId] });
      qc.invalidateQueries({ queryKey: ['meets', meetId, 'registrations-grid'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'meet', meetId] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  if (meetQuery.isLoading || studentsQuery.isLoading || eventsQuery.isLoading) return <LoadingFallback />;
  const meet = meetQuery.data;
  if (!meet) return <EmptyState title="Meet not found" />;

  const parentCoverage = students.filter((s) => s.parents.length > 0).length;
  const missingTimes = events.filter((ev) => !ev.scheduled_start_at).length;
  const eventCountByStudent = students.map((s) => ({ student: s, count: selected[s.id]?.size ?? 0 }));
  const overloaded = eventCountByStudent.filter((x) => x.count > 4);

  function toggle(studentId: number, eventId: number) {
    setSelected((prev) => {
      const copy = { ...prev };
      const set = new Set(copy[studentId] ?? []);
      if (set.has(eventId)) set.delete(eventId);
      else set.add(eventId);
      copy[studentId] = set;
      return copy;
    });
  }

  return (
    <Box>
      <PageHeader
        title={`Prepare ${meet.name}`}
        subtitle="Build events, assign athletes, check parent coverage, then print/send/run the meet."
        actions={
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to={`/meets/${meetId}/coach-pack`} startIcon={<PrintIcon />} variant="outlined">Coach Pack</Button>
            <Button component={RouterLink} to={`/notifications?meet=${meetId}`} startIcon={<EmailIcon />} variant="outlined">Email Parents</Button>
            <Button component={RouterLink} to={`/meets/${meetId}/day`} startIcon={<PlayCircleIcon />} variant="contained">Meet Day</Button>
          </Stack>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ChecklistCard label="Athletes" value={students.length} ok={students.length > 0} />
        <ChecklistCard label="Parent coverage" value={`${parentCoverage}/${students.length}`} ok={students.length === 0 || parentCoverage === students.length} />
        <ChecklistCard label="Events" value={events.length} ok={events.length > 0} />
        <ChecklistCard label="Missing times" value={missingTimes} ok={missingTimes === 0} />
      </Grid>

      {students.length > 0 && parentCoverage < students.length && (
        <Alert severity={parentCoverage === 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
          Parent emails only send to athletes with linked parent emails. {parentCoverage === 0
            ? 'None of your athletes can receive parent schedule emails yet.'
            : `${students.length - parentCoverage} athlete${students.length - parentCoverage === 1 ? '' : 's'} still need a parent email.`}
        </Alert>
      )}

      {overloaded.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {overloaded.map((x) => x.student.full_name).join(', ')} entered in more than 4 events.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6">Event templates</Typography>
              <Typography variant="body2" color="text.secondary">Add a starter event list, then adjust times and divisions on the meet page.</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button onClick={() => addTemplate.mutate(MIDDLE_SCHOOL_CODES)} disabled={addTemplate.isPending}>Middle school</Button>
              <Button onClick={() => addTemplate.mutate(HIGH_SCHOOL_CODES)} disabled={addTemplate.isPending}>High school</Button>
              <Button onClick={() => addTemplate.mutate(YOUTH_CODES)} disabled={addTemplate.isPending}>Youth club</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">Entry grid</Typography>
              <Typography variant="body2" color="text.secondary">Rows are athletes, columns are meet events. Check boxes to enter athletes.</Typography>
            </Box>
            <Button variant="contained" onClick={() => saveGrid.mutate()} disabled={saveGrid.isPending || events.length === 0}>
              Save entries
            </Button>
          </Stack>
          {students.length === 0 || events.length === 0 ? (
            <EmptyState title="Roster or events missing" description="Import athletes and add events before assigning entries." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 190 }}>Athlete</TableCell>
                    {events.map((ev) => (
                      <TableCell key={ev.id} align="center" sx={{ minWidth: 110 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{ev.event_type.code}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">{ev.division}</Typography>
                      </TableCell>
                    ))}
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.grade || 'No grade'} · {s.parents.length ? `${s.parents.length} parent` : 'No parent email'}
                        </Typography>
                      </TableCell>
                      {events.map((ev) => (
                        <TableCell key={ev.id} align="center">
                          <Checkbox
                            checked={selected[s.id]?.has(ev.id) ?? false}
                            onChange={() => toggle(s.id, ev.id)}
                            inputProps={{ 'aria-label': `${s.full_name} in ${ev.event_type.name}` }}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <Chip size="small" color={(selected[s.id]?.size ?? 0) > 4 ? 'warning' : 'default'} label={selected[s.id]?.size ?? 0} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function ChecklistCard({ label, value, ok }: { label: string; value: number | string; ok: boolean }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignmentTurnedInIcon color={ok ? 'success' : 'disabled'} />
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          </Stack>
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}
